<template>
  <b-modal v-model="visible" centered size="md" hide-header hide-footer :no-close-on-backdrop="loading"
    :no-close-on-esc="loading">
    <div class="ign-modal">
      <!-- ===== IGNORE ===== -->
      <template v-if="mode === 'ignore'">
        <p class="ign-modal__title">Ignore this domain?</p>

        <div class="ign-modal__domain">
          <span class="ign-modal__domain-name">{{ domainName || '-' }}</span>
          <span class="ign-modal__risk">
            Risk Score
            <span class="ign-modal__badge" :class="riskClass">{{ riskScore || '-' }}</span>
          </span>
        </div>

        <p class="ign-modal__text">
          This domain will no longer be counted in domain monitoring and will not trigger Telegram alerts.
          Its risk score stays visible and new scans are still recorded.
        </p>

        <div v-if="isPublicPlatform" class="ign-modal__warning">
          <i class="fas fa-exclamation-triangle ign-modal__warning-icon"></i>
          <span><strong>{{ hostname }}</strong> is a public / shortener platform. Counterfeit sellers can use it too.
          Ignoring it hides every link on this platform from monitoring and Telegram alerts.</span>
        </div>

        <label class="ign-modal__label" for="ign-reason">Reason (optional)</label>
        <textarea id="ign-reason" class="ign-modal__textarea" rows="3" maxlength="200" v-model="reason"
          placeholder="e.g. Official domain confirmed by AHM"></textarea>
        <div class="ign-modal__counter">{{ reason.length }}/200</div>
      </template>

      <!-- ===== UNIGNORE ===== -->
      <template v-else>
        <p class="ign-modal__title">Unignore this domain?</p>

        <div class="ign-modal__domain">
          <span class="ign-modal__domain-name">{{ domainName || '-' }}</span>
          <span class="ign-modal__risk">
            Risk Score
            <span class="ign-modal__badge" :class="riskClass">{{ riskScore || '-' }}</span>
          </span>
        </div>

        <p class="ign-modal__text">
          This domain will be counted in domain monitoring again and new scans can trigger Telegram alerts.
          Alerts suppressed while it was ignored are not re-sent.
        </p>
      </template>

      <div class="ign-modal__footer">
        <button type="button" class="ign-modal__btn ign-modal__btn--cancel" :disabled="loading"
          @click="visible = false">Cancel</button>
        <button type="button" class="ign-modal__btn"
          :class="mode === 'ignore' && isPublicPlatform ? 'ign-modal__btn--warn' : 'ign-modal__btn--confirm'"
          :disabled="loading" @click="onConfirm">
          <b-spinner v-if="loading" small></b-spinner>
          <span v-else>{{ confirmLabel }}</span>
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
// Hostname ternormalisasi (BR-01): lowercase, tanpa scheme, www., port, path, query.
// Hanya dipakai untuk teks peringatan. Penentuan "platform publik" datang dari BE (is_public_platform).
export function normalizeHostname(raw) {
  let h = String(raw || '').trim().toLowerCase()
  h = h.replace(/^[a-z][a-z0-9+.-]*:\/\//, '')
  h = h.split(/[/?#]/)[0]
  h = h.replace(/:\d+$/, '').replace(/^www\./, '')
  return h
}

export default {
  name: 'IgnoreDomainModal',
  props: {
    value: { type: Boolean, default: false }, // v-model: show/hide
    mode: { type: String, default: 'ignore' }, // 'ignore' | 'unignore'
    domainName: { type: String, default: '' },
    riskScore: { type: String, default: '' },
    isPublicPlatform: { type: Boolean, default: false }, // dari BE: is_public_platform
    loading: { type: Boolean, default: false },
  },
  data() {
    return { reason: '' }
  },
  computed: {
    visible: {
      get() { return this.value },
      set(v) { this.$emit('input', v) },
    },
    hostname() {
      return normalizeHostname(this.domainName)
    },
    riskClass() {
      const s = (this.riskScore || '').toLowerCase()
      if (s === 'high') return 'ign-modal__badge--high'
      if (s === 'medium') return 'ign-modal__badge--medium'
      if (s === 'low') return 'ign-modal__badge--low'
      return ''
    },
    confirmLabel() {
      if (this.mode === 'unignore') return 'Unignore'
      return this.isPublicPlatform ? 'Ignore anyway' : 'Ignore domain'
    },
  },
  watch: {
    value(show) {
      if (show) this.reason = ''
    },
  },
  methods: {
    onConfirm() {
      if (this.loading) return // cegah double click
      this.$emit('confirm', this.reason.trim())
    },
  },
}
</script>

<style scoped>
.ign-modal {
  padding: 8px 16px 16px;
}

.ign-modal__title {
  font-size: 16px;
  font-weight: 600;
  color: #000;
  margin: 0 0 12px;
  text-align: left;
}

/* Domain dalam kotak, Risk Score di kanan (design 02 / 06) */
.ign-modal__domain {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  padding: 10px 12px;
  background: #F7F8F9;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
}

.ign-modal__risk {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  font-size: 12px;
  color: #6c6c6c;
}

.ign-modal__domain-name {
  font-size: 14px;
  font-weight: 600;
  color: #4b4b4b;
  word-break: break-all;
}

.ign-modal__badge {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
  color: #fff;
  background: #9d9d9d;
}

.ign-modal__badge--high { background: #D0202E; }
.ign-modal__badge--medium { background: #FDB022; }
.ign-modal__badge--low { background: #12B76A; }

.ign-modal__text {
  font-size: 13px;
  color: #6c6c6c;
  text-align: left;
  margin: 0 0 12px;
}

.ign-modal__warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #FFFAEB;
  border: 1px solid #FEC84B;
  color: #93370D;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 12px;
  margin-bottom: 12px;
}

.ign-modal__warning-icon {
  margin-top: 2px;
  color: #DC6803;
}

.ign-modal__label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #4b4b4b;
  margin-bottom: 4px;
}

.ign-modal__textarea {
  width: 100%;
  border: 1px solid #e8e8e8;
  border-radius: 5px;
  padding: 8px 10px;
  font-size: 12px;
  color: #4b4b4b;
  resize: none;
  outline: none;
}

.ign-modal__textarea:focus {
  border-color: #267ce5;
}

.ign-modal__counter {
  text-align: right;
  font-size: 11px;
  color: #9d9d9d;
  margin-top: 2px;
}

/* Tombol di kanan bawah, lebar mengikuti isi (design 02 / 06) */
.ign-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  width: 100%;
  padding: 14px 0 0;
}

.ign-modal__btn {
  min-width: 96px;
  padding: 10px 16px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  text-align: center;
  white-space: nowrap;
}

.ign-modal__btn--cancel {
  background: #fff;
  border: 1px solid #267ce5;
  color: #267ce5;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.1);
}

.ign-modal__btn--confirm {
  background: #267ce5;
  border: 1px solid #e8e8e8;
  color: #fff;
  box-shadow: 0 0 1.5px rgba(0, 0, 0, 0.1);
}

.ign-modal__btn--warn {
  background: #DC6803;
  border: 1px solid #e8e8e8;
  color: #fff;
}

.ign-modal__btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>